/** @file quadGeomOf: an impostor's drawn quad, pivot and tile axes on screen, a running quad drag applied. */
import type { Profile } from '@zilo/pose3d-format/types/profile';
import type { Camera, ImpostorList } from '@zilo/pose3d-render-core/contract';
import { corners } from '@zilo/pose3d-render-core/corners/corners';
import { IMPOSTOR_LAYOUT as L } from '@zilo/pose3d-render-core/layout/impostorLayout';
import { pictureQuadOf } from './pictureQuadOf';
import type { QuadEdit } from './viewportCtx';

type P2 = [number, number];

/**
 * Target px: the quad `[tl, tr, br, bl]` and the pivot where the picture shows them (`pictureQuadOf`: on the rim of
 * the drawn pixels), the tile's screen axes per tile pixel (`u` along the drawn width, `v` along the height, from the
 * exact quad), whether the image is mirrored on them (flags 1 and 2) and the drawn tile size.
 */
export type QuadGeom = { corners: [P2, P2, P2, P2]; pivot: P2; u: P2; v: P2; flip: [boolean, boolean]; size: P2 };

/** One image axis of a drag in the drawn quad's local frame (0..size from tl): the kept range and the pivot. */
function localAxis(e: QuadEdit, k: 0 | 1, size: number, rowPivot: number, flip: boolean): { from: number; to: number; pivot: number } {
  const resize = e.handle !== 8;
  // resize: the new tile spans [P0 - P', P0 - P' + size'] in the old image's pixels; the pivot stays on P0
  const lo = resize ? e.pivot0[k] - e.pivot[k] : 0;
  const hi = resize ? lo + e.size[k] : size;
  const pivot = resize ? rowPivot : rowPivot + (flip ? -1 : 1) * (e.pivot[k] - e.pivot0[k]);
  return flip ? { from: size - hi, to: size - lo, pivot } : { from: lo, to: hi, pivot };
}

/**
 * render-core's `corners` for impostor `i` (the quad the runtime draws), moved onto the drawn pixels by
 * `pictureQuadOf` so every overlay that reads it (quads, outline, handles) sits on the picture's rim. With `edit` on
 * this impostor's drawable the quad and pivot show the drag's result in the same frame: a resize keeps the pivot and
 * moves the pulled edges, a pivot drag keeps the quad and moves the pivot (both in whole or half tile pixels).
 */
export function quadGeomOf(list: ImpostorList, i: number, camera: Camera, profile: Profile, edit: QuadEdit | null): QuadGeom {
  const o = i * L.stride;
  const at = (k: number): number => list[o + k] ?? 0;
  const c = corners(list, i, camera, profile).corners;
  const size: P2 = [at(L.texW), at(L.texH)];
  const flags = at(L.flags);
  const flip: [boolean, boolean] = [(flags & 1) !== 0, (flags & 2) !== 0];
  const u: P2 = [(c[1][0] - c[0][0]) / (size[0] || 1), (c[1][1] - c[0][1]) / (size[0] || 1)];
  const v: P2 = [(c[3][0] - c[0][0]) / (size[1] || 1), (c[3][1] - c[0][1]) / (size[1] || 1)];
  const local = (x: number, y: number): P2 => [c[0][0] + u[0] * x + v[0] * y, c[0][1] + u[1] * x + v[1] * y];
  const rowPivot: P2 = [at(L.pivotX), at(L.pivotY)];
  if (edit === null || edit.drawableIndex !== at(L.drawable)) {
    return { ...pictureQuadOf(c, local(rowPivot[0], rowPivot[1])), u, v, flip, size };
  }
  const x = localAxis(edit, 0, size[0], rowPivot[0], flip[0]);
  const y = localAxis(edit, 1, size[1], rowPivot[1], flip[1]);
  const quad = [local(x.from, y.from), local(x.to, y.from), local(x.to, y.to), local(x.from, y.to)];
  return { ...pictureQuadOf(quad, local(x.pivot, y.pivot)), u, v, flip, size };
}
